import { TOption, valueOf } from "@/types/common";

/**
 * 商品設定 client-side enums.
 *
 * VALUES mirror the backend enums in `irohana-account-backend/src/common/utils/constant.ts`
 * (EBillingCycle / EInvoiceDisplayFormat) — that is the contract the frontend
 * sends/receives. LABELS are mirrored so the UI can render synchronously; in production resolve
 * them via `useEnum(<EnumName>)` (names noted below) so the backend stays the single source.
 *
 *   請求サイクル (billingCycle)          -> server enum: "EBillingCycle"
 *   請求書表示形式 (invoiceDisplayFormat) -> server enum: "EInvoiceDisplayFormat"
 */

/** 請求サイクル — EBillingCycle. */
export const BILLING_CYCLE = {
  /** スポット — one-off charge. */
  SPOT: "spot",
  /** 継続 — recurring charge. */
  RECURRING: "recurring",
} as const;

export type TBillingCycle = valueOf<typeof BILLING_CYCLE>;

export const BILLING_CYCLE_LABEL: Record<TBillingCycle, string> = {
  [BILLING_CYCLE.SPOT]: "スポット",
  [BILLING_CYCLE.RECURRING]: "継続",
};

export const getBillingCycleLabel = (value?: TBillingCycle | null): string =>
  value ? BILLING_CYCLE_LABEL[value] ?? "" : "";

/** 請求サイクル as radio — 継続 first per Figma (node 279-20249). */
export const BILLING_CYCLE_RADIO_OPTIONS: TOption[] = [
  { value: BILLING_CYCLE.RECURRING, label: BILLING_CYCLE_LABEL[BILLING_CYCLE.RECURRING] },
  { value: BILLING_CYCLE.SPOT, label: BILLING_CYCLE_LABEL[BILLING_CYCLE.SPOT] },
];

/** 単位 — price unit. Fixed choices: 件 / 人 / 式 / 回 / 月. */
export const PRODUCT_UNIT_OPTIONS: TOption[] = ["件", "人", "式", "回", "月"].map((u) => ({ value: u, label: u }));

/** 請求書上の表示形式 — EInvoiceDisplayFormat. */
export const INVOICE_DISPLAY_FORMAT = {
  /** 1項目に集約. */
  AGGREGATED: "aggregated",
  /** 受注別に項目化. */
  ITEMIZED_PER_ORDER: "itemized_per_order",
} as const;

export type TInvoiceDisplayFormat = valueOf<typeof INVOICE_DISPLAY_FORMAT>;

export const INVOICE_DISPLAY_FORMAT_LABEL: Record<TInvoiceDisplayFormat, string> = {
  [INVOICE_DISPLAY_FORMAT.AGGREGATED]: "1項目に集約",
  [INVOICE_DISPLAY_FORMAT.ITEMIZED_PER_ORDER]: "受注別に項目化",
};

export const getInvoiceDisplayFormatLabel = (value?: TInvoiceDisplayFormat | null): string =>
  value ? INVOICE_DISPLAY_FORMAT_LABEL[value] ?? "" : "";

/** 請求書上の表示形式 as radio — short labels per Figma (明細 / 集約, node 279-20249). */
export const INVOICE_DISPLAY_FORMAT_RADIO_OPTIONS: TOption[] = [
  { value: INVOICE_DISPLAY_FORMAT.ITEMIZED_PER_ORDER, label: "明細" },
  { value: INVOICE_DISPLAY_FORMAT.AGGREGATED, label: "集約" },
];

/**
 * 種別単価 dimension — segmented control on the 種別単価設定 / 単価組み合わせ cards.
 * NOTE (C3): ティア is hidden and replaced by その他 when the product is 人材紹介料; ティア is only
 * offered for 固定商品 whose default master row has isTierDiscountTarget = true.
 */
export const PRICE_DIMENSION = {
  /** 国籍 — resolved from master_nationalities. */
  NATIONALITY: "nationality",
  /** 分野 — resolved from master_company_specific_industries. */
  FIELD: "field",
  /** ティア — free text entered per row (C7). */
  TIER: "tier",
  /** その他 — free text, only for 人材紹介料 (replaces ティア). */
  OTHER: "other",
} as const;

export type TPriceDimension = valueOf<typeof PRICE_DIMENSION>;

export const PRICE_DIMENSION_LABEL: Record<TPriceDimension, string> = {
  [PRICE_DIMENSION.NATIONALITY]: "国籍",
  [PRICE_DIMENSION.FIELD]: "分野",
  [PRICE_DIMENSION.TIER]: "ティア",
  [PRICE_DIMENSION.OTHER]: "その他",
};

/** 在職期間 operator — 返金条件設定. */
export const TENURE_OPERATOR = {
  /** 未満 — less than. */
  UNDER: "under",
  /** 以内 — within. */
  WITHIN: "within",
} as const;

export type TTenureOperator = valueOf<typeof TENURE_OPERATOR>;

export const TENURE_OPERATOR_LABEL: Record<TTenureOperator, string> = {
  [TENURE_OPERATOR.UNDER]: "未満",
  [TENURE_OPERATOR.WITHIN]: "以内",
};

export const TENURE_OPERATOR_OPTIONS: TOption[] = Object.values(TENURE_OPERATOR).map((value) => ({
  value,
  label: TENURE_OPERATOR_LABEL[value],
}));

/** 返金額 type — % or 円. */
export const REFUND_TYPE = {
  PERCENT: "percent",
  AMOUNT: "amount",
} as const;

export type TRefundType = valueOf<typeof REFUND_TYPE>;

export const REFUND_TYPE_LABEL: Record<TRefundType, string> = {
  [REFUND_TYPE.PERCENT]: "％",
  [REFUND_TYPE.AMOUNT]: "円",
};

export const REFUND_TYPE_OPTIONS: TOption[] = Object.values(REFUND_TYPE).map((value) => ({
  value,
  label: REFUND_TYPE_LABEL[value],
}));

/** 返金条件設定 — number of fixed rows always rendered (empty rows submit as nothing). */
export const REFUND_ROW_COUNT = 5;

/** 在職期間 select options — 1〜12ヶ月. */
export const REFUND_TENURE_OPTIONS: TOption[] = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}ヶ月`,
}));

/**
 * 固定商品 (default product) whose 初回割引期間 / 初回割引単価 are shown. Confirmed (C2):
 * only 支援委託費.
 */
export const FIRST_DISCOUNT_PRODUCT_NAME = "支援委託費";

/** The product name whose 種別単価 dimension shows その他 instead of ティア (C3). */
export const OTHER_DIMENSION_PRODUCT_NAME = "人材紹介料";

/** Validation limits (C6). Shared by schema + inputs. */
export const PRODUCT_LIMITS = {
  /** 商品名. */
  MAX_NAME: 100,
  /** 単位. */
  MAX_UNIT: 20,
  /** 単価（税抜）and money fields — 999,999,999円, no decimals. */
  MAX_PRICE: 999999999,
  /** 初回割引期間 — months. */
  MAX_MONTHS: 999,
} as const;

/** 単価組み合わせ — same (国籍 × 分野 × ティア/その他) condition entered on more than one row. */
export const COMBINATION_DUPLICATED_MESSAGE = "同じ組み合わせ条件が重複しています。重複を解消してください。";

/** Add-row link label used across the editable price tables (Figma: 行を追加). */
export const ADD_ROW_LABEL = "行を追加";

/** Max height for editable product-form tables; overflow scroll is handled by Table container. */
export const PRODUCT_FORM_TABLE_MAX_HEIGHT = 600;

/** Toasts for the 商品設定 switch (active / deactivate). */
export const PRODUCT_ACTIVE_SUCCESS_MESSAGE = "商品設定を更新しました。";
export const PRODUCT_ACTIVE_ERROR_MESSAGE = "商品設定の更新に失敗しました。";

/** Toasts for delete. */
export const PRODUCT_DELETE_SUCCESS_MESSAGE = "選択した商品を削除しました。";
export const PRODUCT_DELETE_ERROR_MESSAGE = "商品の削除に失敗しました。";
